import React, { useState, useEffect } from 'react';

function TariffsPage({ user, activeSubscription, activeTariff }) {
    const [tariffs, setTariffs] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [subscribing, setSubscribing] = useState(null);
    const API_URL = process.env.REACT_APP_API_URL || '/api';

    useEffect(() => { fetchTariffs(); }, []);
    
    const fetchTariffs = async () => {
        try {
            setError(null);
            const response = await fetch(API_URL + '/tariffs');
            if (!response.ok) throw new Error('Ошибка загрузки тарифов');
            const data = await response.json();
            setTariffs(Array.isArray(data) ? data : []);
        } catch (error) {
            setError(error.message);
        } finally {
            setLoading(false);
        }
    };
    
    const subscribe = async (tariffId) => {
        try {
            setSubscribing(tariffId);
            const token = localStorage.getItem('token');
            if (!token) { alert('Пожалуйста, войдите в аккаунт'); return; }
            
            const response = await fetch(API_URL + '/create-subscription', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json', 'Authorization': 'Bearer ' + token },
                body: JSON.stringify({ tariff_id: tariffId })
            });
            
            if (!response.ok) {
                const err = await response.json();
                throw new Error(err.error || 'Ошибка подписки');
            }
            
            const data = await response.json();
            
            if (data.paymentUrl) {
                window.location.href = data.paymentUrl;
            } else {
                alert('✅ ' + (data.message || 'Тариф активирован!'));
                const userResponse = await fetch(API_URL + '/profile', {
                    headers: { 'Authorization': 'Bearer ' + token }
                });
                if (userResponse.ok) {
                    const userData = await userResponse.json();
                    localStorage.setItem('user', JSON.stringify(userData));
                }
                setTimeout(() => window.location.reload(), 1000);
            }
        } catch (error) {
            alert('Ошибка: ' + error.message);
        } finally {
            setSubscribing(null);
        }
    };
    
    if (loading) return <div className="text-center py-12 text-slate-500">Загрузка тарифов...</div>;
    if (error) return <div className="text-center py-12 text-red-500">Ошибка: {error}</div>;
    if (!tariffs || tariffs.length === 0) return <div className="text-center py-12 text-slate-500">Нет доступных тарифов</div>;

    const isCurrentTariff = (tariffId) => activeTariff && activeTariff.id === tariffId;

    return (
        <div className="max-w-7xl mx-auto">
            <div className="text-center mb-8">
                <h1 className="text-3xl md:text-4xl font-black text-slate-900 mb-3">
                    Тарифы для <span className="text-gold-500">исполнителей</span>
                </h1>
                <p className="text-base text-slate-600 max-w-2xl mx-auto">
                    Выберите подходящий тариф для размещения объявлений о сдаче техники
                </p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
                {tariffs.map((tariff, idx) => {
                    const price = parseFloat(tariff.price);
                    const isFree = price === 0;
                    const isCurrent = isCurrentTariff(tariff.id);
                    const isPopular = idx === 1;
                    
                    return (
                        <div 
                            key={tariff.id}
                            className={
                                'relative bg-white rounded-2xl p-6 border-2 transition-all hover:shadow-lg flex flex-col ' +
                                (isCurrent ? 'border-gold-500 shadow-gold' 
                                : isPopular ? 'border-gold-400 shadow-md'
                                : 'border-slate-200')
                            }
                        >
                            {isCurrent && (
                                <div className="absolute -top-3 left-1/2 -translate-x-1/2 bg-gold-500 text-dark-900 text-xs font-bold px-3 py-1 rounded-full whitespace-nowrap">
                                    ✅ АКТИВЕН
                                </div>
                            )}
                            {isPopular && !isCurrent && (
                                <div className="absolute -top-3 left-1/2 -translate-x-1/2 bg-gold-500 text-dark-900 text-xs font-bold px-3 py-1 rounded-full whitespace-nowrap">
                                    🔥 ПОПУЛЯРНЫЙ
                                </div>
                            )}

                            <div className="text-center mb-4 pt-2">
                                <h3 className="text-lg font-bold text-slate-900 mb-2">{tariff.name}</h3>
                                <div className="flex items-baseline justify-center gap-1">
                                    <span className={'text-3xl font-black ' + (isFree ? 'text-emerald-500' : 'text-gold-500')}>
                                        {isFree ? '0' : price.toLocaleString('ru-RU')}
                                    </span>
                                    <span className="text-lg text-slate-500">₽</span>
                                    <span className="text-sm text-slate-400">/мес</span>
                                </div>
                            </div>

                            <ul className="space-y-2.5 mb-6 flex-1">
                                <li className="flex items-start gap-2 text-sm text-slate-700">
                                    <span className="text-gold-500 mt-0.5">🕐</span>
                                    <span>Срок: <strong>{tariff.duration_days || 30} дней</strong></span>
                                </li>
                                <li className="flex items-start gap-2 text-sm text-slate-700">
                                    <span className="text-gold-500 mt-0.5">📢</span>
                                    <span>Объявлений: <strong>{tariff.max_ads === null || tariff.max_ads >= 999999 ? '∞' : tariff.max_ads}</strong></span>
                                </li>
                               <li className="flex items-start gap-2 text-sm text-slate-700">
                                    <span className="text-gold-500 mt-0.5">💬</span>
                                    <span>Откликов: <strong>{tariff.max_offers === null || tariff.max_offers >= 999999 ? '∞' : tariff.max_offers}</strong></span>
                                </li>
                                <li className="flex items-start gap-2 text-sm text-slate-700">
                                    <span className="text-emerald-500 mt-0.5">✅</span>
                                    <span>Приём онлайн-платежей</span>
                                </li>
                                <li className="flex items-start gap-2 text-sm text-slate-700">
                                    <span className="text-emerald-500 mt-0.5">✅</span>
                                    <span>Гарантия сделки</span>
                                </li>
                            </ul>

                            <button
                                onClick={() => subscribe(tariff.id)}
                                disabled={subscribing === tariff.id || isCurrent}
                                className={
                                    'w-full py-3 rounded-xl font-bold text-sm transition ' +
                                    (isCurrent 
                                        ? 'bg-slate-100 text-slate-400 cursor-not-allowed'
                                        : isPopular || isFree
                                            ? 'bg-gold-500 hover:bg-gold-400 text-dark-900 shadow-gold'
                                            : 'bg-dark-900 hover:bg-dark-800 text-white')
                                }
                            >
                                {subscribing === tariff.id ? 'Обработка...' 
                                    : isCurrent ? '✅ Текущий тариф'
                                    : isFree ? '🎁 Активировать бесплатно'
                                    : '💳 Купить за ' + price.toLocaleString('ru-RU') + ' ₽'}
                            </button>
                        </div>
                    );
                })}
            </div>

            <div className="mt-8 text-center text-sm text-slate-500">
                <p>💡 Все тарифы можно менять в любое время. При смене тарифа старый деактивируется автоматически.</p>
            </div>
        </div>
    );
}

export default TariffsPage;
